<template>
  <AppLayout :breadcrumbs="breadcrumbs">
    <div class="layout-header">
      <div class="header-left">
        <div class="product-info">
          <IconCircle :size="32" bgColor="var(--icon-product-bg)" color="var(--icon-product-color)" :icon="getProductIcon()" />
          <h2 class="product-name">{{ productDisplayName }}</h2>
        </div>

        <TabBar :tabs="tabs" :active-tab="activeTab" @update:active-tab="handleTabChange" />
      </div>

      <div class="header-right">
        <template v-if="mode === 'view' && activeTab === 'details' && currentUser && currentUser.isAdmin">
          <template v-if="product && product.isDeleted">
            <span class="deactivated-badge">Deactivated</span>
            <button class="button primary" @click="handleReactivate" :disabled="isReactivating">
              {{ isReactivating ? 'Reactivating...' : 'Reactivate Product' }}
            </button>
          </template>
          <template v-else>
            <button class="button danger-outline" @click="openModal('delete-product')">Deactivate</button>
            <button class="button primary" @click="enterEditMode">Edit Product</button>
          </template>
        </template>

        <template v-else-if="mode === 'edit' || mode === 'create'">
          <button class="button" @click="cancelEdit">Cancel</button>
          <button class="button primary" @click="saveChanges">
            {{ mode === 'create' ? 'Create Product' : 'Save Changes' }}
          </button>
        </template>
      </div>
    </div>

    <div class="layout-body">
      <div v-if="activeTab === 'details'" class="card-container">
        <CardInputForm title="Overview" v-model="draft" :fields="overviewFields">
          <template #field-name>
            <TextInput v-model="draft.name" placeholder="Enter product name" />
          </template>
          <template #display-name="{ value }">
            <span :class="{ 'no-data': !value }">{{ value || 'No data' }}</span>
          </template>

          <template #field-productType>
            <SelectInput v-model="draft.productType" :options="productTypeOptions" placeholder="Select product type" />
          </template>
          <template #display-productType="{ value }">
            <span :class="{ 'no-data': !value }">{{ formatProductType(value) || 'No data' }}</span>
          </template>

          <template #field-description>
            <TextareaInput v-model="draft.description" placeholder="e.g. A mix of 3 separate drugs." />
          </template>
          <template #display-description="{ value }">
            <div :class="{ 'no-data': !value }" style="white-space: pre-wrap;">{{ value || 'No data' }}</div>
          </template>

          <template #field-defaultDurationMonths>
            <SelectInput v-model="draft.defaultDurationMonths" :options="durationOptions" placeholder="Select duration..." />
          </template>
          <template #display-defaultDurationMonths="{ value }">
            <span :class="{ 'no-data': value === null }">{{ value !== null ? formatDuration(value) : 'No default' }}</span>
          </template>

          <template #field-defaultQuantity>
            <NumericalInput v-model="draft.defaultQuantity" :optional="true" placeholder="Leave empty for no default" />
          </template>
          <template #display-defaultQuantity="{ value }">
            <span :class="{ 'no-data': value === null }">{{ value !== null ? value : 'No default' }}</span>
          </template>

          <template #field-defaultUnitPriceCents>
            <MonetaryInput v-model="draft.defaultUnitPriceCents" :optional="true" placeholder="Leave empty for no default" />
          </template>
          <template #display-defaultUnitPriceCents="{ value }">
            <span :class="{ 'no-data': value === null }">{{ formatUtils.formatCurrency(value) || 'No default' }}</span>
          </template>

        </CardInputForm>

        <div class="card-input-form">
          <div class="card-header">
            <div class="card-title-row">
              <h3 class="card-title">Allowance Tracking</h3>
              <Tooltip text="Set up allowance tracking to define what unit a product is consumed in and how much is included per purchase. E.g. TriMix 25 dose plan = 25 tablets, TRT 24 months = 24 injections." position="right">
                <InformationOutlineIcon :size="16" class="info-icon" />
              </Tooltip>
            </div>
          </div>
          <div class="card-body">
            <div class="form-grid">
              <div class="form-row">
                <label class="form-label">Enabled</label>
                <div class="form-field">
                  <div v-if="!isReadonly" class="form-editor">
                    <SwitchInput v-model="draft.allowanceEnabled" />
                  </div>
                  <div v-else class="form-display">
                    <span>{{ draft.allowanceEnabled ? 'Yes' : 'No' }}</span>
                  </div>
                </div>
              </div>
              <div class="form-row">
                <label class="form-label">
                  Unit
                  <Tooltip text="What is being consumed? E.g. tablet, troche, injection, session, vial, mg, mL" position="right">
                    <InformationOutlineIcon :size="14" class="info-icon" />
                  </Tooltip>
                </label>
                <div class="form-field">
                  <div v-if="!isReadonly" class="form-editor">
                    <TextInput v-model="draft.allowanceUnit" placeholder="e.g. tablet, injection, session" :disabled="!draft.allowanceEnabled" />
                  </div>
                  <div v-else class="form-display">
                    <span :class="{ 'no-data': !draft.allowanceUnit }">{{ draft.allowanceUnit || 'Not set' }}</span>
                  </div>
                </div>
              </div>
              <div class="form-row">
                <label class="form-label">
                  Amount
                  <Tooltip text="How much of that unit is included per purchase? E.g. 25 tablets, 100 injections, 12 sessions" position="right">
                    <InformationOutlineIcon :size="14" class="info-icon" />
                  </Tooltip>
                </label>
                <div class="form-field">
                  <div v-if="!isReadonly" class="form-editor">
                    <NumericalInput v-model="draft.allowanceAmount" :optional="true" placeholder="e.g. 25" :disabled="!draft.allowanceEnabled" />
                  </div>
                  <div v-else class="form-display">
                    <span :class="{ 'no-data': draft.allowanceAmount === null }">{{ formatAllowanceAmount() }}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <CardInputForm title="Invoice Information" v-model="draft" :fields="invoiceFields">
          <template #field-insuranceCodes>
            <TextareaInput v-model="draft.insuranceCodes" placeholder="e.g. NDC: ( Tadalafil 00002-4464-30 (75mg), Vardenafil 00085-1934-01 (75mg) )" />
          </template>
          <template #display-insuranceCodes="{ value }">
            <div :class="{ 'no-data': !value }" style="white-space: pre-wrap;">{{ value || 'No data' }}</div>
          </template>

          <template #field-diagnosisCodes>
            <TextareaInput v-model="draft.diagnosisCodes" placeholder="e.g. N50.1 - Vascular Disorders of the Penis&#10;N52.9 - Impotence of Organic Organ&#10;E29.1 - Testicular Hypo Function" />
          </template>
          <template #display-diagnosisCodes="{ value }">
            <div :class="{ 'no-data': !value }" style="white-space: pre-wrap;">{{ value || 'No data' }}</div>
          </template>
        </CardInputForm>
      </div>
      <div v-if="activeTab === 'tickets'">
        <p>Tickets</p>
      </div>
    </div>

    <!-- Modals -->
    <transition name="modal-fade" mode="out-in">
      <div class="modal-background close-on-click" v-if="modal.open" @click="handleModalClick">
        <div class="modal-scroll close-on-click">
          <transition name="modal-pop" mode="out-in">
            <DeleteProductModal v-if="modal.id === 'delete-product'" :productId="productId" @close="closeModal" @deleted="handleProductDeleted" />
          </transition>
        </div>
      </div>
    </transition>
  </AppLayout>
</template>

<script>
import AppLayout from '@/components/app/AppLayout.vue';
import TabBar from '@/components/common/TabBar.vue';
import CardInputForm from '@/components/common/CardInputForm.vue';
import IconCircle from '@/components/common/IconCircle.vue';
import TextInput from '@/components/inputs/TextInput.vue';
import TextareaInput from '@/components/inputs/TextareaInput.vue';
import SelectInput from '@/components/inputs/SelectInput.vue';
import MonetaryInput from '@/components/inputs/MonetaryInput.vue';
import NumericalInput from '@/components/inputs/NumericalInput.vue';
import SwitchInput from '@/components/inputs/SwitchInput.vue';
import Tooltip from '@/components/common/Tooltip.vue';
import InformationOutlineIcon from 'vue-material-design-icons/InformationOutline.vue';
import PillIcon from 'vue-material-design-icons/Pill.vue';
import CurrencyUsdIcon from 'vue-material-design-icons/CurrencyUsd.vue';
import PackageIcon from 'vue-material-design-icons/Package.vue';
import DeleteProductModal from '@/modals/DeleteProductModal.vue';
import * as api from '@/api';
import * as formatUtils from '@/utils/formatters';
import { queryClient } from '@/libs/queryClient';
import { alertService } from '@/libs/alert';

export default {
  name: 'ProductRecordView',
  computed: {
    productId() {
      return this.$route.params.id;
    },
    productLink() {
      return '/products/' + this.productId;
    },
    breadcrumbs() {
      return [
        { label: 'Products', link: '/products' },
        { label: this.productDisplayName || 'Product', link: this.productLink }
      ];
    },
    productDisplayName() {
      if (this.mode === 'create') return 'New Product';
      if (!this.product) return 'Loading...';
      return this.product.name || 'Unknown Product';
    },
    isReadonly() {
      return this.mode === 'view';
    },
    durationOptions() {
      return [
        { value: 0, label: 'None' },
        { value: 1, label: '1 mo' },
        { value: 2, label: '2 mo' },
        { value: 3, label: '3 mo' },
        { value: 4, label: '4 mo' },
        { value: 5, label: '5 mo' },
        { value: 6, label: '6 mo' },
        { value: 7, label: '7 mo' },
        { value: 8, label: '8 mo' },
        { value: 9, label: '9 mo' },
        { value: 10, label: '10 mo' },
        { value: 11, label: '11 mo' },
        { value: 12, label: '12 mo' }
      ];
    },
    productTypeOptions() {
      return [
        { value: 'medication', label: 'Medication' },
        { value: 'fee', label: 'Fee' },
        { value: 'other', label: 'Other' }
      ];
    },
    overviewFields() {
      return [
        {
          name: 'name',
          label: 'Product Name',
          readonly: this.isReadonly || this.mode === 'edit'
        },
        {
          name: 'productType',
          label: 'Product Type',
          readonly: this.isReadonly
        },
        {
          name: 'description',
          label: 'Product Description',
          tooltip: 'Printed on the invoice under this line item. Use it for a brief explanation of the product.',
          readonly: this.isReadonly
        },
        {
          name: 'defaultDurationMonths',
          label: 'Default Duration',
          tooltip: 'Pre-fills the duration when this product is added to a ticket. The counselor can still change it.',
          readonly: this.isReadonly
        },
        {
          name: 'defaultQuantity',
          label: 'Default Quantity',
          tooltip: 'Pre-fills the quantity when this product is added to a ticket. Also used to calculate the total price (quantity x unit price).',
          readonly: this.isReadonly
        },
        {
          name: 'defaultUnitPriceCents',
          label: 'Default Unit Price',
          tooltip: 'Price per unit. When a product is added to a ticket, total price is calculated as quantity x unit price.',
          readonly: this.isReadonly
        },
      ];
    },
    invoiceFields() {
      return [
        {
          name: 'insuranceCodes',
          label: 'Insurance Codes',
          tooltip: 'NDC or other insurance codes printed on the invoice. Displayed in the Codes column.',
          readonly: this.isReadonly
        },
        {
          name: 'diagnosisCodes',
          label: 'Diagnosis Codes',
          tooltip: 'ICD-10 or other diagnosis codes printed at the bottom of the invoice.',
          readonly: this.isReadonly
        }
      ];
    },
    tabs() {
      return [
        { key: 'details', label: 'Details' }
      ];
    },
    activeTab() {
      const tab = this.$route.params.tab;
      const validTabs = this.tabs.map(t => t.key);
      if (tab && validTabs.includes(tab)) {
        return tab;
      }
      return 'details';
    }
  },
  data() {
    return {
      product: null,
      isLoading: false,
      isReactivating: false,
      mode: 'view',
      draft: {},
      currentUser: null,
      formatUtils,
      modal: {
        open: false,
        id: ''
      }
    };
  },
  created() {
    this.currentUser = queryClient.getQueryData(['user']) || null;
    if (this.productId === 'new') {
      this.enterCreateMode();
    } else {
      this.load();
    }
  },
  methods: {
    async load() {
      try {
        this.isLoading = true;

        const product = await queryClient.fetchQuery({
          queryKey: ['product', this.productId],
          queryFn: () => api.fetchProductById(this.productId)
        });

        this.product = product;
        this.draft = structuredClone(this.product);

      } catch (err) {
        console.error('Failed to fetch product:', err);
        alertService.error('Failed to load product: ' + err.message);
        this.$router.push('/products');
      } finally {
        this.isLoading = false;
      }
    },
    handleTabChange(tabKey) {
      const basePath = `/products/${this.productId}`;
      if (tabKey === 'details') {
        this.$router.push(basePath);
      } else {
        this.$router.push(`${basePath}/${tabKey}`);
      }
    },
    enterEditMode() {
      if (this.activeTab !== 'details') {
        this.$router.push(`/products/${this.productId}`);
      }
      this.draft = structuredClone(this.product);
      this.mode = 'edit';
    },
    enterCreateMode() {
      this.draft = this.getEmptyDraft();
      this.mode = 'create';
    },
    cancelEdit() {
      if (this.mode === 'create') {
        this.$router.push('/products');
      } else {
        this.draft = structuredClone(this.product);
        this.mode = 'view';
      }
    },
    getEmptyDraft() {
      return {
        name: '',
        productType: 'other',
        defaultQuantity: null,
        defaultDurationMonths: null,
        defaultUnitPriceCents: null,
        allowanceEnabled: false,
        allowanceUnit: null,
        allowanceAmount: null,
        diagnosisCodes: null,
        description: null,
        insuranceCodes: null
      };
    },
    getProductIcon() {
      const productType = this.draft?.productType || this.product?.productType || 'other';
      switch (productType) {
        case 'medication':
          return PillIcon;
        case 'fee':
          return CurrencyUsdIcon;
        case 'other':
        default:
          return PackageIcon;
      }
    },
    formatDuration(months) {
      if (!months || months === 0) return 'None';
      return `${months} mo`;
    },
    formatAllowanceAmount() {
      if (this.draft.allowanceAmount === null) return 'Not set';
      if (this.draft.allowanceUnit) return `${this.draft.allowanceAmount} ${this.draft.allowanceUnit}`;
      return String(this.draft.allowanceAmount);
    },
    formatProductType(value) {
      const option = this.productTypeOptions.find(opt => opt.value === value);
      return option ? option.label : value;
    },
    transformDraftToPayload(draft) {
      return {
        name: draft.name,
        productType: draft.productType,
        defaultQuantity: draft.defaultQuantity,
        defaultDurationMonths: draft.defaultDurationMonths,
        defaultUnitPriceCents: draft.defaultUnitPriceCents,
        allowanceEnabled: draft.allowanceEnabled,
        allowanceUnit: draft.allowanceEnabled ? draft.allowanceUnit : null,
        allowanceAmount: draft.allowanceEnabled ? draft.allowanceAmount : null,
        diagnosisCodes: draft.diagnosisCodes,
        description: draft.description,
        insuranceCodes: draft.insuranceCodes
      };
    },
    openModal(id) {
      this.modal.open = true;
      setTimeout(() => {
        this.modal.id = id;
      });
    },
    closeModal() {
      this.modal.id = '';
      setTimeout(() => {
        this.modal.open = false;
      });
    },
    handleModalClick(event) {
      if (event.target.classList.contains('close-on-click')) {
        this.closeModal();
      }
    },
    handleProductDeleted() {
      this.closeModal();
      queryClient.invalidateQueries({ queryKey: ['products'] });
      alertService.success('Product deactivated successfully');
      this.$router.push('/products');
    },
    async handleReactivate() {
      try {
        this.isReactivating = true;
        const updated = await api.reactivateProduct(this.productId);
        this.product = updated;
        this.draft = structuredClone(updated);
        queryClient.invalidateQueries({ queryKey: ['products'] });
        queryClient.invalidateQueries({ queryKey: ['product', this.productId] });
        alertService.success('Product reactivated successfully');
      } catch (err) {
        console.error('Failed to reactivate product:', err);
        alertService.error('Failed to reactivate product: ' + err.message);
      } finally {
        this.isReactivating = false;
      }
    },
    async saveChanges() {
      try {
        const payload = this.transformDraftToPayload(this.draft);
        let updatedProduct;

        if (this.mode === 'edit') {
          updatedProduct = await api.updateProduct(this.productId, payload);
          alertService.success('Product updated successfully');
        }

        if (this.mode === 'create') {
          updatedProduct = await api.createProduct(payload);
          alertService.success('Product created successfully');
          this.$router.replace(`/products/${updatedProduct.productId}`);
        }

        // Invalidate caches
        queryClient.invalidateQueries({ queryKey: ['products'] });
        queryClient.invalidateQueries({ queryKey: ['product', this.productId] });

        // Update product
        this.product = updatedProduct;
        this.draft = structuredClone(this.product);
        this.mode = 'view';

      } catch (err) {
        console.error('Failed to save product:', err);
        alertService.error('Failed to save product: ' + err.message);
      }
    }
  },
  components: { AppLayout, TabBar, CardInputForm, IconCircle, TextInput, TextareaInput, SelectInput, MonetaryInput, NumericalInput, SwitchInput, Tooltip, InformationOutlineIcon, DeleteProductModal }
}
</script>

<style scoped>
.header-left {
  display: flex;
  align-items: center;
  gap: 24px;
  flex: 1;
  min-width: 0;
}

.header-right {
  display: flex;
  align-items: center;
  gap: 12px;
}

.product-info {
  display: flex;
  align-items: center;
  gap: 12px;
}

.product-name {
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  color: #262626;
  line-height: 1.2;
}

.card-container {
  display: grid;
  grid-template-columns: 1fr;
  gap: 0;
}

.layout-header .button {
  white-space: nowrap;
}

.no-data {
  color: #9ca3af;
  font-style: italic;
}

.card-input-form {
  background: #FFF;
}

.card-header {
  padding: 16px;
  background-color: var(--content-header-bg);
  border-bottom: var(--content-header-border);
}

.card-title-row {
  display: flex;
  align-items: center;
  gap: 8px;
}

.card-title {
  margin: 0;
  font-size: 14px;
  font-weight: 600;
  color: #262626;
}

.card-body {
  padding: 16px;
}

.form-grid {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.form-row {
  display: flex;
  gap: 16px;
  align-items: start;
}

.form-row .form-label {
  font-weight: 500;
  color: #333;
  line-height: 31px;
  flex-basis: 200px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  gap: 6px;
}

.form-row .form-field {
  width: 1px;
  flex: 1;
}

.form-row .form-editor {
  max-width: 400px;
}

.form-row .form-display {
  max-width: 400px;
  color: #333;
  height: 31px;
  display: flex;
  align-items: center;
  padding: 0 2px;
}

.info-icon {
  color: #9ca3af;
  cursor: help;
}

.info-icon:hover {
  color: #6b7280;
}

.button.danger-outline {
  color: #dc2626;
  border-color: #dc2626;
  background: white;
}

.button.danger-outline:hover {
  background-color: #fef2f2;
}

.deactivated-badge {
  display: inline-flex;
  align-items: center;
  padding: 4px 10px;
  font-size: 12px;
  font-weight: 600;
  color: #92400e;
  background-color: #fef3c7;
  border: 1px solid #f59e0b;
  border-radius: var(--border-radius);
  text-transform: uppercase;
  letter-spacing: 0.05em;
}
</style>
